import * as v from 'valibot';

/** @type {number} */
export const PERSON_NAME_MAX = 80;

/** @type {number} */
export const ORG_NAME_MAX = 120;

/** @type {number} */
export const TITLE_SUBJECT_MAX = 200;

/** @type {number} */
export const SHORT_LABEL_MAX = 64;

/** @type {number} */
export const ADDRESS_LINE_MAX = 200;

/** @type {number} */
export const DESCRIPTION_MAX = 5000;

/** @type {string} */
export const INVALID_CHARACTERS_MESSAGE = 'Contains invalid characters';

const BIDI_OVERRIDE = /[\u202A-\u202E\u2066-\u2069]/;
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/;
const PERSON_NAME_CHARS = /^[\p{L}\p{M}'.\- ]+$/u;
const ORG_NAME_CHARS = /^[\p{L}\p{M}\p{N}'.\- #&(),/]+$/u;
const HAS_LETTER = /\p{L}/u;
const DISTRICT_NUMBER_CHARS = /^[\p{L}\p{N}-]+$/u;
const SINGLE_LINE_EXTRA_FORBIDDEN = /[<>{}]/;
const MULTILINE_EXTRA_FORBIDDEN = /[<>{}]/;

/**
 * @param {string} value
 * @param {Set<string>} [allowedChars]
 * @returns {boolean}
 */
function hasDisallowedControlChars(value, allowedChars = new Set()) {
  for (const char of value) {
    if (allowedChars.has(char)) {
      continue;
    }
    const code = char.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

/**
 * Trim and collapse internal whitespace for single-line plain text.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeSingleLineText(value) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Returns true when a single-line value contains forbidden characters.
 *
 * @param {string} value Normalized single-line text
 * @returns {boolean}
 */
export function hasForbiddenSingleLineChars(value) {
  return (
    hasDisallowedControlChars(value) ||
    SINGLE_LINE_EXTRA_FORBIDDEN.test(value) ||
    BIDI_OVERRIDE.test(value) ||
    ZERO_WIDTH.test(value) ||
    /[\r\n]/.test(value)
  );
}

/**
 * Remove characters forbidden in single-line text (control chars, bidi overrides, etc.).
 *
 * @param {string} value
 * @returns {string}
 */
function stripForbiddenSingleLineChars(value) {
  let result = '';
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f) {
      continue;
    }
    if (SINGLE_LINE_EXTRA_FORBIDDEN.test(char)) {
      continue;
    }
    if (BIDI_OVERRIDE.test(char) || ZERO_WIDTH.test(char)) {
      continue;
    }
    if (char === '\r' || char === '\n') {
      continue;
    }
    result += char;
  }
  return result;
}

/**
 * Normalize and sanitize a filename or display title for storage and Content-Disposition.
 * Strips the same forbidden character classes as {@link hasForbiddenSingleLineChars}.
 *
 * @param {unknown} name
 * @param {string} [fallback='document']
 * @returns {string}
 */
export function sanitizeFilenameForDisplay(name, fallback = 'document') {
  const normalized = normalizeSingleLineText(name);
  if (!normalized) {
    return fallback;
  }

  const sanitized = stripForbiddenSingleLineChars(normalized).trim();
  return sanitized || fallback;
}

/**
 * Returns true when a multiline value contains forbidden characters.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function hasForbiddenMultilineChars(value) {
  return (
    hasDisallowedControlChars(value, new Set(['\n', '\t'])) ||
    MULTILINE_EXTRA_FORBIDDEN.test(value) ||
    BIDI_OVERRIDE.test(value) ||
    ZERO_WIDTH.test(value)
  );
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidPersonNameChars(value) {
  return PERSON_NAME_CHARS.test(value) && HAS_LETTER.test(value);
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidOrgNameChars(value) {
  return ORG_NAME_CHARS.test(value) && HAS_LETTER.test(value);
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidDistrictNumber(value) {
  return DISTRICT_NUMBER_CHARS.test(value) && value.length > 0;
}

/**
 * @param {object} [options]
 * @param {number} [options.maxLength]
 * @param {number} [options.minLength]
 * @param {string} [options.minLengthMessage]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function singleLineBasicField({
  maxLength = ADDRESS_LINE_MAX,
  minLength = 1,
  minLengthMessage = 'This field is required',
} = {}) {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), INVALID_CHARACTERS_MESSAGE),
    v.minLength(minLength, minLengthMessage),
    v.maxLength(maxLength, `Must be at most ${maxLength} characters`),
  );
}

/**
 * Optional single-line field: empty string or validated basic text.
 *
 * @param {object} [options]
 * @param {number} [options.maxLength]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function optionalSingleLineBasicField({ maxLength = SHORT_LABEL_MAX } = {}) {
  return v.union([v.literal(''), singleLineBasicField({ maxLength, minLength: 1 })]);
}

/**
 * Full person display name (registration, profile, invites). Min 2 characters.
 *
 * @param {object} [options]
 * @param {string} [options.minLengthMessage]
 * @param {string} [options.maxLengthMessage]
 * @param {string} [options.invalidMessage]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function personDisplayNameField({
  minLengthMessage = 'Name must be at least 2 characters',
  maxLengthMessage = `Name must be at most ${PERSON_NAME_MAX} characters`,
  invalidMessage = INVALID_CHARACTERS_MESSAGE,
} = {}) {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), invalidMessage),
    v.minLength(2, minLengthMessage),
    v.maxLength(PERSON_NAME_MAX, maxLengthMessage),
    v.check(isValidPersonNameChars, invalidMessage),
  );
}

/**
 * Person name part (first/last name). Min 1 character for single-character given names.
 *
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function personNamePartField() {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), INVALID_CHARACTERS_MESSAGE),
    v.minLength(1, 'Name is required'),
    v.maxLength(PERSON_NAME_MAX, `Name must be at most ${PERSON_NAME_MAX} characters`),
    v.check(isValidPersonNameChars, INVALID_CHARACTERS_MESSAGE),
  );
}

/**
 * Organization or school display name.
 *
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function orgDisplayNameField() {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), INVALID_CHARACTERS_MESSAGE),
    v.minLength(1, 'Name is required'),
    v.maxLength(ORG_NAME_MAX, `Name must be at most ${ORG_NAME_MAX} characters`),
    v.check(isValidOrgNameChars, INVALID_CHARACTERS_MESSAGE),
  );
}

/**
 * Optional organization display name.
 *
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function optionalOrgDisplayNameField() {
  return v.union([v.literal(''), orgDisplayNameField()]);
}

/**
 * Single-line title or subject field.
 *
 * @param {object} [options]
 * @param {number} [options.minLength]
 * @param {number} [options.maxLength]
 * @param {string} [options.minLengthMessage]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function singleLineTitleField({
  minLength = 1,
  maxLength = TITLE_SUBJECT_MAX,
  minLengthMessage = 'This field is required',
} = {}) {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), INVALID_CHARACTERS_MESSAGE),
    v.minLength(minLength, minLengthMessage),
    v.maxLength(maxLength, `Must be at most ${maxLength} characters`),
    v.check(isValidOrgNameChars, INVALID_CHARACTERS_MESSAGE),
  );
}

/**
 * Optional single-line title.
 *
 * @param {object} [options]
 * @param {number} [options.maxLength]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function optionalSingleLineTitleField({ maxLength = TITLE_SUBJECT_MAX } = {}) {
  return v.union([v.literal(''), singleLineTitleField({ maxLength, minLength: 1 })]);
}

/**
 * Multiline plain text (descriptions, notes). Allows newlines and tabs.
 *
 * @param {object} [options]
 * @param {number} [options.maxLength]
 * @param {number} [options.minLength]
 * @param {string} [options.minLengthMessage]
 * @param {string} [options.maxLengthMessage]
 * @param {string} [options.invalidMessage]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function plainMultilineField({
  maxLength = DESCRIPTION_MAX,
  minLength = 1,
  minLengthMessage = 'This field is required',
  maxLengthMessage = `Must be at most ${maxLength} characters`,
  invalidMessage = INVALID_CHARACTERS_MESSAGE,
} = {}) {
  return v.pipe(
    v.string(),
    v.transform((value) => String(value ?? '').trim()),
    v.check((value) => !hasForbiddenMultilineChars(value), invalidMessage),
    v.minLength(minLength, minLengthMessage),
    v.maxLength(maxLength, maxLengthMessage),
  );
}

/**
 * Optional multiline plain text.
 *
 * @param {object} [options]
 * @param {number} [options.maxLength]
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function optionalPlainMultilineField({ maxLength = DESCRIPTION_MAX } = {}) {
  return v.union([v.literal(''), plainMultilineField({ maxLength, minLength: 1 })]);
}

/**
 * District number (alphanumeric and hyphen).
 *
 * @returns {import('valibot').BaseSchema<string, string, import('valibot').BaseIssue<unknown>>}
 */
export function districtNumberField() {
  return v.pipe(
    v.string(),
    v.transform(normalizeSingleLineText),
    v.check((value) => !hasForbiddenSingleLineChars(value), INVALID_CHARACTERS_MESSAGE),
    v.minLength(1, 'District number is required'),
    v.maxLength(
      SHORT_LABEL_MAX,
      `District number must be at most ${SHORT_LABEL_MAX} characters`,
    ),
    v.check(isValidDistrictNumber, INVALID_CHARACTERS_MESSAGE),
  );
}
